export function portableTextToPlain(value: unknown): string {
  if (typeof value === "string") return value;
  if (!Array.isArray(value)) return "";

  let hasInlineIcon = false;
  const text = value
    .filter(
      (
        block,
      ): block is {
        _type: string;
        children?: Array<{ text?: string; _type?: string }>;
      } => block?._type === "block",
    )
    .map((block) =>
      (block.children ?? [])
        .map((child) => {
          if (child?._type === "inlineIcon") {
            hasInlineIcon = true;
            return "";
          }
          return child?.text ?? "";
        })
        .join(""),
    )
    .join(" ")
    .trim();

  if (text) return text;
  return hasInlineIcon ? " " : "";
}

type PortableTextBlockChildLike = {
  text?: string;
  _type?: string;
};

type PortableTextBlockLike = {
  _type: string;
  children?: PortableTextBlockChildLike[];
};

/**
 * Truncates a Portable Text value at the first period, keeping the period.
 * Iterates blocks in order, slicing the first text block whose concatenated
 * spans contain a period, and drops everything after it.
 */
export function truncatePortableTextAtFirstPeriod<
  T extends PortableTextBlockLike,
>(blocks: T[]): T[] {
  const firstSentenceRegex = /^[\s\S]*?\./;
  const result: T[] = [];

  for (const block of blocks) {
    if (block._type !== "block" || !block.children) {
      result.push(block);
      continue;
    }

    const fullText = block.children.map((span) => span.text ?? "").join("");
    const match = firstSentenceRegex.exec(fullText);

    if (!match) {
      result.push(block);
      continue;
    }

    let remaining = match[0].length;
    const truncatedChildren: typeof block.children = [];
    for (const span of block.children) {
      const text = span.text ?? "";
      if (text.length <= remaining) {
        truncatedChildren.push(span);
        remaining -= text.length;
        if (remaining === 0) break;
      } else {
        truncatedChildren.push({ ...span, text: text.slice(0, remaining) });
        break;
      }
    }

    result.push({ ...block, children: truncatedChildren });
    return result;
  }

  return result;
}
